import React, { useEffect } from 'react';

import Icon from 'views/site/Icon.js';

import CoverageQuiz from 'views/site/CoverageQuiz.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * QuizModal
 *
 * Modal host for the coverage walkthrough. A modal rather than a page because
 * the walkthrough is something you start from wherever you already are — the
 * hero, the product grid, either call to action — and sending someone to a
 * separate URL loses the place they were reading.
 *
 * The quiz is only mounted while open, so closing it and reopening starts a
 * fresh walkthrough rather than resuming a half-answered one from an hour ago.
 */

const QuizModal = ({ dealership, onAddPlanToQuote, onClose, open, quiz }) => {

    const mobile = Utils.isMobile();

    useEffect(() => {
        if(!open) {
            return;
        }
        const onKeyDown = evt => evt.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    // The body must not scroll behind a full-height modal on a phone
    useEffect(() => {
        document.body.style.overflowY = open ? 'hidden' : 'scroll';
        return () => { document.body.style.overflowY = 'scroll'; };
    }, [open]);

    return (
        <>
            <div
            className={`site-overlay ${open ? 'active' : ''}`}
            onClick={onClose} />

            <div
            aria-hidden={open === false}
            className={'site'}
            style={{
                alignItems: 'center',
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                left: 0,
                padding: mobile ? 12 : 20,
                pointerEvents: open ? 'all' : 'none',

                /*
                 * visibility is discrete, and transitioning it is what makes the
                 * exit visible at all. Without this it flips to hidden on the
                 * same frame the close is pressed, so the 300ms fade underneath
                 * plays to nobody. Transitioned, the browser applies `visible`
                 * immediately on the way in and defers `hidden` to the end on the
                 * way out — which is exactly the asymmetry this needs.
                 */
                transition: 'visibility 0.3s',
                visibility: open ? 'visible' : 'hidden',
                position: 'fixed',
                right: 0,
                top: 0,
                zIndex: 6500
            }}>
                <div
                onClick={evt => evt.stopPropagation()}
                style={{
                    backgroundColor: Theme.colors.white,
                    borderRadius: 24,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                    maxHeight: '90vh',
                    maxWidth: 900,
                    opacity: open ? 1 : 0,
                    overflowY: 'auto',
                    position: 'relative',
                    transform: open ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.98)',
                    transition: 'opacity 0.3s ease, transform 0.3s ease',
                    width: '100%'
                }}>
                    <div
                    onClick={onClose}
                    style={{
                        alignItems: 'center',
                        backgroundColor: Theme.colors.offWhite,
                        borderRadius: 18,
                        cursor: 'pointer',
                        display: 'flex',
                        height: 36,
                        justifyContent: 'center',
                        position: 'absolute',
                        right: 20,
                        top: 20,
                        width: 36,
                        zIndex: 2
                    }}>
                        <Icon name={'x-lg'} size={14} color={Theme.colors.darkGrey} />
                    </div>

                    <div style={{ padding: mobile ? '36px 22px' : '44px 44px' }}>
                        <span style={{
                            ...Theme.textStyles.label(),
                            display: 'block',
                            marginBottom: 10
                        }}>{'Build your network'}</span>
                        <span style={{
                            ...Theme.textStyles.heading({ fontSize: mobile ? 24 : 30 }),
                            display: 'block',
                            marginBottom: 8
                        }}>{'Let’s walk your home'}</span>
                        <span style={{
                            ...Theme.textStyles.body({ fontSize: 15 }),
                            display: 'block',
                            marginBottom: 32,
                            maxWidth: 560
                        }}>{`About a minute. We go room by room, the same way ${dealership?.name || 'we'} would in person, and end with the exact devices your home needs and why each one is there.`}</span>

                        {/* Mounted whether or not the modal is open. Unmounting
                            it threw away the walkthrough every time someone
                            closed the sheet to go and look at something, which
                            is exactly when they were most likely to come back
                            to it. "Start over" inside the quiz is the way to
                            reset, and it is deliberate. */}
                        <CoverageQuiz
                        dealership={dealership}
                        initialAnswers={quiz}
                        onAddToQuote={onAddPlanToQuote} />
                    </div>
                </div>
            </div>
        </>
    )
}

export default QuizModal;
